import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  marketingApi,
  type AssetResponse,
  type AssetCategory,
  type AssetLabel,
} from "@/services/api/marketing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as FormLabel } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  DraggableTree,
  TreeItemRow,
} from "@/components/ui/draggable-tree";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pagination } from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus,
  Upload,
  Image as ImageIcon,
  Pencil,
  Trash2,
  RefreshCw,
  Loader2,
  MoreHorizontal,
  FileImage,
  Tag,
  FolderTree,
  X,
  Edit,
  Folder,
  FolderOpen,
} from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import {
  usePaginatedData,
  type PaginatedResponse,
} from "@/hooks/usePaginatedData";

export const Route = createFileRoute("/portal/asset-library")({
  component: AssetLibraryRoute,
});

// Internal tooling (sidebar "Internal" group): platform admins only. Same
// guard as /portal/ai-content — the sidebar hides the item, this stops a
// direct URL from rendering the page.
function AssetLibraryRoute() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const isPlatformAdmin = user?.is_admin === true;

  useEffect(() => {
    if (!isLoading && !isPlatformAdmin) {
      navigate({ to: "/portal" });
    }
  }, [isLoading, isPlatformAdmin, navigate]);

  if (isLoading || !isPlatformAdmin) {
    return null;
  }

  return <AssetLibraryPage />;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const PRESET_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4",
  "#3b82f6", "#8b5cf6", "#ec4899", "#6b7280",
];

// ---------------------------------------------------------------------------
// Multi-select picker for categories / labels (used in upload & edit dialogs)
// ---------------------------------------------------------------------------

function MultiSelectPicker({
  items,
  selectedIds,
  onToggle,
  label,
  colourField,
}: {
  items: { id: string; name: string; colour?: string | null }[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  label: string;
  colourField?: boolean;
}) {
  return (
    <div>
      <FormLabel>{label}</FormLabel>
      <div className="mt-1.5 flex flex-wrap gap-1.5 rounded-md border p-2 min-h-[38px]">
        {items.length === 0 && (
          <span className="text-xs text-muted-foreground">None available</span>
        )}
        {items.map((item) => {
          const selected = selectedIds.includes(item.id);
          return (
            <Badge
              key={item.id}
              variant={selected ? "default" : "outline"}
              className="cursor-pointer select-none"
              style={
                selected && colourField && item.colour
                  ? { backgroundColor: item.colour, borderColor: item.colour }
                  : undefined
              }
              onClick={() => onToggle(item.id)}
            >
              {item.name}
              {selected && <X className="ml-1 h-3 w-3" />}
            </Badge>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function AssetLibraryPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const _organisationId = selectedOrganisation?.organisationId || "";
  // Categories & labels (fetched from API)
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [labels, setLabels] = useState<AssetLabel[]>([]);

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [labelFilter, setLabelFilter] = useState<string>("all");
  const [assetTypeFilter, setAssetTypeFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Sort state
  const [sortBy, setSortBy] = useState<string>("CreatedAtUtc");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Dialog state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<AssetResponse | null>(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showBulkCategoryDialog, setShowBulkCategoryDialog] = useState(false);
  const [showBulkLabelDialog, setShowBulkLabelDialog] = useState(false);
  const [showManageCategoriesDialog, setShowManageCategoriesDialog] = useState(false);
  const [showManageLabelsDialog, setShowManageLabelsDialog] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetResponse | null>(null);
  const [uploading, setUploading] = useState(false);

  // Upload form state
  const [uploadCategoryIds, setUploadCategoryIds] = useState<string[]>([]);
  const [uploadLabelIds, setUploadLabelIds] = useState<string[]>([]);
  const [uploadAltText, setUploadAltText] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  // Edit form state
  const [editCategoryIds, setEditCategoryIds] = useState<string[]>([]);
  const [editLabelIds, setEditLabelIds] = useState<string[]>([]);
  const [editAltText, setEditAltText] = useState("");
  const [saving, setSaving] = useState(false);

  // Bulk assign state
  const [bulkCategoryIds, setBulkCategoryIds] = useState<string[]>([]);
  const [bulkLabelIds, setBulkLabelIds] = useState<string[]>([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  // Category management state
  const [categorySearch, setCategorySearch] = useState("");
  const [createCategoryDialogOpen, setCreateCategoryDialogOpen] = useState(false);
  const [editCategoryDialogOpen, setEditCategoryDialogOpen] = useState(false);
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: "", description: "", parent_id: "", colour: "" });
  const [editCategoryItem, setEditCategoryItem] = useState<AssetCategory | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<AssetCategory | null>(null);
  const [savingCategory, setSavingCategory] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(false);

  // Label management state
  const [createLabelDialogOpen, setCreateLabelDialogOpen] = useState(false);
  const [editLabelDialogOpen, setEditLabelDialogOpen] = useState(false);
  const [deleteLabelDialogOpen, setDeleteLabelDialogOpen] = useState(false);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColour, setNewLabelColour] = useState("");
  const [editLabelItem, setEditLabelItem] = useState<AssetLabel | null>(null);
  const [labelToDelete, setLabelToDelete] = useState<AssetLabel | null>(null);
  const [savingLabel, setSavingLabel] = useState(false);
  const [deletingLabel, setDeletingLabel] = useState(false);

  // Clear selection on filter/sort change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchTerm, categoryFilter, labelFilter, assetTypeFilter, sortBy, sortOrder]);

  // Load categories and labels
  const loadTaxonomies = useCallback(async () => {
    if (!token) return;
    try {
      const [cats, lbls] = await Promise.all([
        marketingApi.listCategories(token),
        marketingApi.listLabels(token),
      ]);
      setCategories(cats);
      setLabels(lbls);
    } catch {
      // Silently fail — dropdowns will just be empty
    }
  }, [token]);

  useEffect(() => {
    loadTaxonomies();
  }, [loadTaxonomies]);

  // Sort helpers
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const getSortDirection = (column: string): "asc" | "desc" | null => {
    return sortBy === column ? sortOrder : null;
  };

  // Paginated data — server-side search + sort
  const fetchAssets = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<AssetResponse>> => {
      if (!token) return { data: [], totalCount: 0, page, pageSize };

      const offset = (page - 1) * pageSize;
      const resp = await marketingApi.listAssets(token, {
        limit: pageSize,
        offset,
        category_id: categoryFilter !== "all" ? categoryFilter : undefined,
        label_id: labelFilter !== "all" ? labelFilter : undefined,
        asset_type: assetTypeFilter !== "all" ? assetTypeFilter : undefined,
        search: searchTerm || undefined,
        sort_by: sortBy as "CreatedAtUtc" | "Filename" | "FileSizeBytes",
        sort_order: sortOrder,
      });

      return {
        data: resp.items,
        totalCount: resp.total,
        page,
        pageSize,
      };
    },
    [token, categoryFilter, labelFilter, assetTypeFilter, searchTerm, sortBy, sortOrder],
  );

  const {
    data: assets,
    isLoading,
    pagination,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<AssetResponse>({
    fetchFn: fetchAssets,
    initialPageSize: 25,
    dependencies: [categoryFilter, labelFilter, assetTypeFilter, searchTerm, sortBy, sortOrder],
  });

  // Bulk selection helpers
  const allOnPageSelected =
    assets.length > 0 && assets.every((a) => selectedIds.has(a.id));
  const someOnPageSelected =
    assets.some((a) => selectedIds.has(a.id)) && !allOnPageSelected;

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        assets.forEach((a) => next.delete(a.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        assets.forEach((a) => next.add(a.id));
        return next;
      });
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Toggle helper for multi-select
  const toggleId = (ids: string[], id: string) =>
    ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];

  // Upload single asset
  const handleUpload = async () => {
    if (!token || !selectedFile) return;
    setUploading(true);
    try {
      await marketingApi.uploadAsset(
        token,
        selectedFile,
        uploadCategoryIds,
        uploadLabelIds,
        uploadAltText || undefined,
      );
      toast.success("Upload accepted — processing in background");
      resetUploadForm();
      setShowUploadDialog(false);
      // Poll for results: refresh every 5s for up to 2 minutes
      let polls = 0;
      const maxPolls = 24;
      const pollInterval = setInterval(() => {
        polls++;
        refresh();
        if (polls >= maxPolls) clearInterval(pollInterval);
      }, 5000);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to upload asset",
      );
    } finally {
      setUploading(false);
    }
  };

  // Bulk upload
  const handleBulkUpload = async () => {
    if (!token || selectedFiles.length === 0) return;
    setUploading(true);
    try {
      const resp = await marketingApi.bulkUploadAssets(
        token,
        selectedFiles,
        uploadCategoryIds,
        uploadLabelIds,
        uploadAltText || undefined,
      );
      const count = resp.job_ids.length;
      toast.success(`${count} upload(s) accepted — processing in background`);
      resetUploadForm();
      setShowBulkUploadDialog(false);
      // Poll for results: refresh every 5s for up to 2 minutes
      let polls = 0;
      const maxPolls = 24;
      const pollInterval = setInterval(() => {
        polls++;
        refresh();
        if (polls >= maxPolls) clearInterval(pollInterval);
      }, 5000);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to upload assets",
      );
    } finally {
      setUploading(false);
    }
  };

  // Edit asset
  const handleEdit = async () => {
    if (!token || !selectedAsset) return;
    setSaving(true);
    try {
      await marketingApi.updateAsset(token, selectedAsset.id, {
        category_ids: editCategoryIds,
        label_ids: editLabelIds,
        alt_text: editAltText || undefined,
      });
      toast.success("Asset updated");
      setShowEditDialog(false);
      refresh();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update asset",
      );
    } finally {
      setSaving(false);
    }
  };

  // Delete asset
  const handleDelete = async () => {
    if (!token || !selectedAsset) return;
    try {
      await marketingApi.deleteAsset(token, selectedAsset.id);
      toast.success("Asset deleted");
      setShowDeleteDialog(false);
      setSelectedAsset(null);
      refresh();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete asset",
      );
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (!token || selectedIds.size === 0) return;
    setBulkSaving(true);
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((id) => marketingApi.deleteAsset(token, id)));
      toast.success(`${ids.length} asset(s) deleted`);
      setSelectedIds(new Set());
      setShowBulkDeleteDialog(false);
      refresh();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete assets",
      );
    } finally {
      setBulkSaving(false);
    }
  };

  // Bulk assign categories
  const handleBulkAssignCategory = async () => {
    if (!token || selectedIds.size === 0 || bulkCategoryIds.length === 0) return;
    setBulkSaving(true);
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(
        ids.map((id) =>
          marketingApi.updateAsset(token, id, { category_ids: bulkCategoryIds }),
        ),
      );
      toast.success(`Categories assigned to ${ids.length} asset(s)`);
      setShowBulkCategoryDialog(false);
      setBulkCategoryIds([]);
      refresh();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to assign categories",
      );
    } finally {
      setBulkSaving(false);
    }
  };

  // Bulk assign labels
  const handleBulkAssignLabel = async () => {
    if (!token || selectedIds.size === 0 || bulkLabelIds.length === 0) return;
    setBulkSaving(true);
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(
        ids.map((id) =>
          marketingApi.updateAsset(token, id, { label_ids: bulkLabelIds }),
        ),
      );
      toast.success(`Labels assigned to ${ids.length} asset(s)`);
      setShowBulkLabelDialog(false);
      setBulkLabelIds([]);
      refresh();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to assign labels",
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setSelectedFiles([]);
    setUploadCategoryIds([]);
    setUploadLabelIds([]);
    setUploadAltText("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
  };

  const openEditDialog = (asset: AssetResponse) => {
    setSelectedAsset(asset);
    setEditCategoryIds(asset.categories.map((c) => c.id));
    setEditLabelIds(asset.labels.map((l) => l.id));
    setEditAltText(asset.alt_text || "");
    setShowEditDialog(true);
  };

  // ---- Category management ----

  const filteredCategories = useMemo(() => {
    if (!categorySearch) return categories;
    const search = categorySearch.toLowerCase();
    return categories.filter(
      (cat) =>
        cat.name.toLowerCase().includes(search) ||
        cat.description?.toLowerCase().includes(search),
    );
  }, [categories, categorySearch]);

  const defaultExpandedIds = useMemo(
    () => categories.filter((c) => !c.parent_id).map((c) => c.id),
    [categories],
  );

  const handleCategoryMove = useCallback(
    async (itemId: string, newParentId: string | null, newIndex: number) => {
      if (!token) return;

      const previousCategories = [...categories];
      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === itemId
            ? { ...cat, parent_id: newParentId, sort_order: newIndex }
            : cat,
        ),
      );

      try {
        await marketingApi.updateCategory(token, itemId, {
          parent_id: newParentId,
          sort_order: newIndex,
        });
        toast.success("Category moved");
        loadTaxonomies();
      } catch {
        toast.error("Failed to move category");
        setCategories(previousCategories);
      }
    },
    [token, categories, loadTaxonomies],
  );

  const handleCreateCategory = async () => {
    if (!token || !newCategory.name.trim()) return;
    setSavingCategory(true);
    try {
      await marketingApi.createCategory(token, {
        name: newCategory.name.trim(),
        description: newCategory.description || undefined,
        parent_id: newCategory.parent_id || undefined,
        colour: newCategory.colour || undefined,
      });
      toast.success("Category created");
      setCreateCategoryDialogOpen(false);
      setNewCategory({ name: "", description: "", parent_id: "", colour: "" });
      loadTaxonomies();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create category");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleEditCategory = async () => {
    if (!token || !editCategoryItem) return;
    if (!editCategoryItem.name.trim()) {
      toast.error("Category name is required");
      return;
    }
    setSavingCategory(true);
    try {
      await marketingApi.updateCategory(token, editCategoryItem.id, {
        name: editCategoryItem.name,
        description: editCategoryItem.description ?? undefined,
        colour: editCategoryItem.colour ?? undefined,
      });
      toast.success("Category updated");
      setEditCategoryDialogOpen(false);
      setEditCategoryItem(null);
      loadTaxonomies();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update category");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!token || !categoryToDelete) return;
    setDeletingCategory(true);
    try {
      await marketingApi.deleteCategory(token, categoryToDelete.id);
      toast.success("Category deleted");
      setDeleteCategoryDialogOpen(false);
      setCategoryToDelete(null);
      loadTaxonomies();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete category");
    } finally {
      setDeletingCategory(false);
    }
  };

  // ---- Label management ----

  const handleCreateLabel = async () => {
    if (!token || !newLabelName.trim()) return;
    setSavingLabel(true);
    try {
      await marketingApi.createLabel(token, {
        name: newLabelName.trim(),
        colour: newLabelColour || undefined,
      });
      toast.success("Label created");
      setCreateLabelDialogOpen(false);
      setNewLabelName("");
      setNewLabelColour("");
      loadTaxonomies();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create label");
    } finally {
      setSavingLabel(false);
    }
  };

  const handleEditLabel = async () => {
    if (!token || !editLabelItem) return;
    if (!editLabelItem.name.trim()) {
      toast.error("Label name is required");
      return;
    }
    setSavingLabel(true);
    try {
      await marketingApi.updateLabel(token, editLabelItem.id, {
        name: editLabelItem.name,
        colour: editLabelItem.colour ?? undefined,
      });
      toast.success("Label updated");
      setEditLabelDialogOpen(false);
      setEditLabelItem(null);
      loadTaxonomies();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update label");
    } finally {
      setSavingLabel(false);
    }
  };

  const handleDeleteLabel = async () => {
    if (!token || !labelToDelete) return;
    setDeletingLabel(true);
    try {
      await marketingApi.deleteLabel(token, labelToDelete.id);
      toast.success("Label deleted");
      setDeleteLabelDialogOpen(false);
      setLabelToDelete(null);
      loadTaxonomies();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete label");
    } finally {
      setDeletingLabel(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Asset Library</h2>
          <p className="text-sm text-muted-foreground">
            Manage curated assets for category-based content generation
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowManageCategoriesDialog(true)}
          >
            <FolderTree className="mr-2 h-4 w-4" />
            Categories
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowManageLabelsDialog(true)}
          >
            <Tag className="mr-2 h-4 w-4" />
            Labels
          </Button>
          <Button
            size="sm"
            onClick={() => {
              resetUploadForm();
              setShowBulkUploadDialog(true);
            }}
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload Assets
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm("")}
          placeholder="Search by filename..."
          containerClassName="flex-1 min-w-[200px]"
        />
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={labelFilter} onValueChange={setLabelFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Labels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Labels</SelectItem>
            {labels.map((l) => (
              <SelectItem key={l.id} value={l.id}>
                {l.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={assetTypeFilter} onValueChange={setAssetTypeFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="image">Images</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="icon" onClick={refresh} title="Refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium">
            {selectedIds.size} item{selectedIds.size !== 1 ? "s" : ""} selected
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setBulkCategoryIds([]);
              setShowBulkCategoryDialog(true);
            }}
          >
            <FolderTree className="h-4 w-4 mr-2" />
            Assign Category
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setBulkLabelIds([]);
              setShowBulkLabelDialog(true);
            }}
          >
            <Tag className="h-4 w-4 mr-2" />
            Assign Label
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-destructive border-destructive/30 hover:bg-destructive/10"
            onClick={() => setShowBulkDeleteDialog(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds(new Set())}
          >
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : assets.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <FileImage className="h-12 w-12 text-muted-foreground" />
          <p className="text-muted-foreground">
            No assets found. Upload assets to get started.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={
                        allOnPageSelected
                          ? true
                          : someOnPageSelected
                            ? "indeterminate"
                            : false
                      }
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all assets on this page"
                    />
                  </TableHead>
                  <SortableTableHead
                    direction={getSortDirection("Filename")}
                    onSort={() => handleSort("Filename")}
                  >
                    Filename
                  </SortableTableHead>
                  <TableHead>Categories</TableHead>
                  <TableHead>Labels</TableHead>
                  <TableHead>Dimensions</TableHead>
                  <SortableTableHead
                    direction={getSortDirection("FileSizeBytes")}
                    onSort={() => handleSort("FileSizeBytes")}
                  >
                    Size
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("CreatedAtUtc")}
                    onSort={() => handleSort("CreatedAtUtc")}
                  >
                    Created
                  </SortableTableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.map((asset) => (
                  <TableRow
                    key={asset.id}
                    className={`group ${selectedIds.has(asset.id) ? "bg-primary/5" : ""}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(asset.id)}
                        onCheckedChange={() => toggleSelect(asset.id)}
                        aria-label={`Select ${asset.filename}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <button
                        className="flex items-center gap-2 hover:underline text-left"
                        onClick={() => setPreviewAsset(asset)}
                      >
                        {asset.thumbnail_url ? (
                          <img
                            src={asset.thumbnail_url}
                            alt={asset.alt_text || asset.filename}
                            className="h-8 w-8 rounded object-cover shrink-0"
                          />
                        ) : (
                          <ImageIcon className="h-8 w-8 text-muted-foreground shrink-0 p-1" />
                        )}
                        <span className="truncate max-w-[200px]" title={asset.filename}>
                          {asset.filename}
                        </span>
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap max-w-[180px]">
                        {asset.categories.length > 0 ? (
                          asset.categories.slice(0, 2).map((c) => (
                            <Badge key={c.id} variant="outline" className="text-xs">
                              {c.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                        {asset.categories.length > 2 && (
                          <span className="text-xs text-muted-foreground">
                            +{asset.categories.length - 2}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap max-w-[150px]">
                        {asset.labels.length > 0 ? (
                          asset.labels.slice(0, 2).map((l) => (
                            <Badge
                              key={l.id}
                              variant="secondary"
                              className="text-xs"
                              style={
                                l.colour
                                  ? { backgroundColor: l.colour, color: "#fff" }
                                  : undefined
                              }
                            >
                              {l.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                        {asset.labels.length > 2 && (
                          <span className="text-xs text-muted-foreground">
                            +{asset.labels.length - 2}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {asset.original_width && asset.original_height
                        ? `${asset.original_width} x ${asset.original_height}`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatFileSize(asset.file_size_bytes)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(asset.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(asset)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setSelectedAsset(asset);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pagination.totalCount > 0 && (
            <div className="mt-4 border-t pt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                pageSize={pagination.pageSize}
                pageSizeOptions={[25, 50, 100]}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                onPageChange={goToPage}
                onPageSizeChange={setPageSize}
                onFirstPage={firstPage}
                onLastPage={lastPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          )}
        </>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Upload Asset</DialogTitle>
            <DialogDescription>
              Upload a single image to the library. JPEG or PNG, min 1200px
              shortest side, max 50MB, RGB only. Images are automatically
              resized and compressed on upload.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <FormLabel>Image File</FormLabel>
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={(e) =>
                  setSelectedFile(e.target.files?.[0] || null)
                }
              />
            </div>
            <MultiSelectPicker
              items={categories}
              selectedIds={uploadCategoryIds}
              onToggle={(id) => setUploadCategoryIds(toggleId(uploadCategoryIds, id))}
              label="Categories"
            />
            <MultiSelectPicker
              items={labels}
              selectedIds={uploadLabelIds}
              onToggle={(id) => setUploadLabelIds(toggleId(uploadLabelIds, id))}
              label="Labels"
              colourField
            />
            <div>
              <FormLabel>Alt Text</FormLabel>
              <Input
                value={uploadAltText}
                onChange={(e) => setUploadAltText(e.target.value)}
                placeholder="Description of the image"
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUploadDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
            >
              {uploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkUploadDialog} onOpenChange={setShowBulkUploadDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Upload Assets</DialogTitle>
            <DialogDescription>
              Upload one or more images. JPEG or PNG, min 1200px shortest
              side, max 50MB each, RGB only. Images are automatically resized
              and compressed on upload.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <FormLabel>Image Files</FormLabel>
              <Input
                ref={bulkFileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                multiple
                onChange={(e) =>
                  setSelectedFiles(
                    e.target.files ? Array.from(e.target.files) : [],
                  )
                }
              />
              {selectedFiles.length > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {selectedFiles.length} file(s) selected
                </p>
              )}
            </div>
            <MultiSelectPicker
              items={categories}
              selectedIds={uploadCategoryIds}
              onToggle={(id) => setUploadCategoryIds(toggleId(uploadCategoryIds, id))}
              label="Categories"
            />
            <MultiSelectPicker
              items={labels}
              selectedIds={uploadLabelIds}
              onToggle={(id) => setUploadLabelIds(toggleId(uploadLabelIds, id))}
              label="Labels"
              colourField
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkUploadDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkUpload}
              disabled={uploading || selectedFiles.length === 0}
            >
              {uploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Upload {selectedFiles.length > 0
                ? `${selectedFiles.length} File(s)`
                : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Asset</DialogTitle>
            <DialogDescription>
              Update metadata for {selectedAsset?.filename}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <MultiSelectPicker
              items={categories}
              selectedIds={editCategoryIds}
              onToggle={(id) => setEditCategoryIds(toggleId(editCategoryIds, id))}
              label="Categories"
            />
            <MultiSelectPicker
              items={labels}
              selectedIds={editLabelIds}
              onToggle={(id) => setEditLabelIds(toggleId(editLabelIds, id))}
              label="Labels"
              colourField
            />
            <div>
              <FormLabel>Alt Text</FormLabel>
              <Input
                value={editAltText}
                onChange={(e) => setEditAltText(e.target.value)}
                placeholder="Description of the image"
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedAsset?.filename}&quot;? This
              will remove the asset from the library and its blob storage. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Asset(s)</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.size} selected asset(s)?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkSaving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkSaving ? "Deleting..." : "Delete All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Assign Category Dialog */}
      <Dialog open={showBulkCategoryDialog} onOpenChange={setShowBulkCategoryDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Assign Categories</DialogTitle>
            <DialogDescription>
              Assign categories to {selectedIds.size} selected asset(s).
            </DialogDescription>
          </DialogHeader>
          <MultiSelectPicker
            items={categories}
            selectedIds={bulkCategoryIds}
            onToggle={(id) => setBulkCategoryIds(toggleId(bulkCategoryIds, id))}
            label="Categories"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkCategoryDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkAssignCategory}
              disabled={bulkSaving || bulkCategoryIds.length === 0}
            >
              {bulkSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Label Dialog */}
      <Dialog open={showBulkLabelDialog} onOpenChange={setShowBulkLabelDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Assign Labels</DialogTitle>
            <DialogDescription>
              Assign labels to {selectedIds.size} selected asset(s).
            </DialogDescription>
          </DialogHeader>
          <MultiSelectPicker
            items={labels}
            selectedIds={bulkLabelIds}
            onToggle={(id) => setBulkLabelIds(toggleId(bulkLabelIds, id))}
            label="Labels"
            colourField
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkLabelDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkAssignLabel}
              disabled={bulkSaving || bulkLabelIds.length === 0}
            >
              {bulkSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================ */}
      {/* Manage Categories Dialog — DraggableTree                         */}
      {/* ================================================================ */}
      <Dialog open={showManageCategoriesDialog} onOpenChange={setShowManageCategoriesDialog}>
        <DialogContent className="sm:max-w-[550px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderTree className="h-5 w-5" />
              Manage Categories
            </DialogTitle>
            <DialogDescription>
              Drag to reorder or reparent. Click the menu on each item to edit or delete.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <SearchInput
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              onClear={() => setCategorySearch("")}
              placeholder="Search categories..."
              containerClassName="flex-1"
            />
            <Button
              size="sm"
              onClick={() => {
                setNewCategory({ name: "", description: "", parent_id: "", colour: "" });
                setCreateCategoryDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {categories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <FolderTree className="h-10 w-10 text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">No categories yet</p>
              </div>
            ) : categorySearch ? (
              <div className="space-y-1">
                {filteredCategories.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No categories match your search
                  </p>
                ) : (
                  filteredCategories.map((cat) => (
                    <div
                      key={cat.id}
                      className="flex items-center justify-between rounded-md border px-3 py-2 group"
                    >
                      <div className="flex items-center gap-2">
                        {cat.colour && (
                          <div
                            className="h-3 w-3 rounded-full shrink-0"
                            style={{ backgroundColor: cat.colour }}
                          />
                        )}
                        <span className="text-sm font-medium">{cat.name}</span>
                        {cat.description && (
                          <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                            — {cat.description}
                          </span>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setEditCategoryItem({ ...cat });
                              setEditCategoryDialogOpen(true);
                            }}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setCategoryToDelete(cat);
                              setDeleteCategoryDialogOpen(true);
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <DraggableTree
                items={categories}
                idKey="id"
                parentKey="parent_id"
                sortKey="sort_order"
                onMove={handleCategoryMove}
                defaultExpandedIds={defaultExpandedIds}
                allowReparent
                indentPx={24}
                renderItem={(item, _node, props) => (
                  <TreeItemRow
                    dragHandleProps={props.dragHandleProps}
                    isExpanded={props.isExpanded}
                    hasChildren={props.hasChildren}
                    toggleExpand={props.toggleExpand}
                    className={props.isOver ? "bg-primary/5" : ""}
                  >
                    <div className="flex items-center justify-between w-full group">
                      <div className="flex items-center gap-2 min-w-0">
                        {props.isExpanded && props.hasChildren ? (
                          <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                        ) : (
                          <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                        {item.colour && (
                          <div
                            className="h-3 w-3 rounded-full shrink-0"
                            style={{ backgroundColor: item.colour }}
                          />
                        )}
                        <span className="text-sm font-medium truncate">
                          {item.name}
                        </span>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 shrink-0"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setEditCategoryItem({ ...item });
                              setEditCategoryDialogOpen(true);
                            }}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setCategoryToDelete(item);
                              setDeleteCategoryDialogOpen(true);
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TreeItemRow>
                )}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Category Dialog */}
      <Dialog open={createCategoryDialogOpen} onOpenChange={setCreateCategoryDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <FormLabel>Name</FormLabel>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="Category name"
                onKeyDown={(e) => e.key === "Enter" && handleCreateCategory()}
              />
            </div>
            <div>
              <FormLabel>Description</FormLabel>
              <Input
                value={newCategory.description}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div>
              <FormLabel>Parent Category</FormLabel>
              <Select
                value={newCategory.parent_id || "none"}
                onValueChange={(v) => setNewCategory({ ...newCategory, parent_id: v === "none" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="None (root level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (root level)</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <FormLabel>Colour</FormLabel>
              <div className="flex items-center gap-2 flex-wrap mt-1.5">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-7 h-7 rounded-full border-2 transition-all ${
                      newCategory.colour === color
                        ? "border-foreground scale-110"
                        : "border-transparent hover:border-muted-foreground/50"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() =>
                      setNewCategory({
                        ...newCategory,
                        colour: newCategory.colour === color ? "" : color,
                      })
                    }
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCategory}
              disabled={savingCategory || !newCategory.name.trim()}
            >
              {savingCategory && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={editCategoryDialogOpen} onOpenChange={setEditCategoryDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          {editCategoryItem && (
            <div className="space-y-4">
              <div>
                <FormLabel>Name</FormLabel>
                <Input
                  value={editCategoryItem.name}
                  onChange={(e) =>
                    setEditCategoryItem({ ...editCategoryItem, name: e.target.value })
                  }
                />
              </div>
              <div>
                <FormLabel>Description</FormLabel>
                <Input
                  value={editCategoryItem.description || ""}
                  onChange={(e) =>
                    setEditCategoryItem({
                      ...editCategoryItem,
                      description: e.target.value || null,
                    })
                  }
                  placeholder="Optional description"
                />
              </div>
              <div>
                <FormLabel>Colour</FormLabel>
                <div className="flex items-center gap-2 flex-wrap mt-1.5">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-7 h-7 rounded-full border-2 transition-all ${
                        editCategoryItem.colour === color
                          ? "border-foreground scale-110"
                          : "border-transparent hover:border-muted-foreground/50"
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() =>
                        setEditCategoryItem({
                          ...editCategoryItem,
                          colour: editCategoryItem.colour === color ? null : color,
                        })
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditCategory} disabled={savingCategory}>
              {savingCategory && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation */}
      <AlertDialog open={deleteCategoryDialogOpen} onOpenChange={setDeleteCategoryDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{categoryToDelete?.name}&quot;?
              Assets in this category will be unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              disabled={deletingCategory}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingCategory ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ================================================================ */}
      {/* Manage Labels Dialog                                              */}
      {/* ================================================================ */}
      <Dialog open={showManageLabelsDialog} onOpenChange={setShowManageLabelsDialog}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Manage Labels
            </DialogTitle>
            <DialogDescription>
              Create, edit, and delete asset labels.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => {
                setNewLabelName("");
                setNewLabelColour("");
                setCreateLabelDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Label
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0 space-y-1">
            {labels.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Tag className="h-10 w-10 text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">No labels yet</p>
              </div>
            ) : (
              labels.map((l) => (
                <div
                  key={l.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2 group"
                >
                  <div className="flex items-center gap-2">
                    {l.colour ? (
                      <div
                        className="h-3.5 w-3.5 rounded-full shrink-0"
                        style={{ backgroundColor: l.colour }}
                      />
                    ) : (
                      <div className="h-3.5 w-3.5 rounded-full shrink-0 border border-muted-foreground/30" />
                    )}
                    <span className="text-sm font-medium">{l.name}</span>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          setEditLabelItem({ ...l });
                          setEditLabelDialogOpen(true);
                        }}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => {
                          setLabelToDelete(l);
                          setDeleteLabelDialogOpen(true);
                        }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Label Dialog */}
      <Dialog open={createLabelDialogOpen} onOpenChange={setCreateLabelDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add Label</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <FormLabel>Name</FormLabel>
              <Input
                value={newLabelName}
                onChange={(e) => setNewLabelName(e.target.value)}
                placeholder="Label name"
                onKeyDown={(e) => e.key === "Enter" && handleCreateLabel()}
              />
            </div>
            <div>
              <FormLabel>Colour</FormLabel>
              <div className="flex items-center gap-2 flex-wrap mt-1.5">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-7 h-7 rounded-full border-2 transition-all ${
                      newLabelColour === color
                        ? "border-foreground scale-110"
                        : "border-transparent hover:border-muted-foreground/50"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() =>
                      setNewLabelColour(newLabelColour === color ? "" : color)
                    }
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateLabelDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateLabel}
              disabled={savingLabel || !newLabelName.trim()}
            >
              {savingLabel && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Label Dialog */}
      <Dialog open={editLabelDialogOpen} onOpenChange={setEditLabelDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit Label</DialogTitle>
          </DialogHeader>
          {editLabelItem && (
            <div className="space-y-4">
              <div>
                <FormLabel>Name</FormLabel>
                <Input
                  value={editLabelItem.name}
                  onChange={(e) =>
                    setEditLabelItem({ ...editLabelItem, name: e.target.value })
                  }
                />
              </div>
              <div>
                <FormLabel>Colour</FormLabel>
                <div className="flex items-center gap-2 flex-wrap mt-1.5">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-7 h-7 rounded-full border-2 transition-all ${
                        editLabelItem.colour === color
                          ? "border-foreground scale-110"
                          : "border-transparent hover:border-muted-foreground/50"
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() =>
                        setEditLabelItem({
                          ...editLabelItem,
                          colour: editLabelItem.colour === color ? null : color,
                        })
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLabelDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditLabel} disabled={savingLabel}>
              {savingLabel && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Label Confirmation */}
      <AlertDialog open={deleteLabelDialogOpen} onOpenChange={setDeleteLabelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Label</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{labelToDelete?.name}&quot;?
              Assets with this label will be unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLabel}
              disabled={deletingLabel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingLabel ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Asset Preview Dialog */}
      <Dialog open={!!previewAsset} onOpenChange={(open) => !open && setPreviewAsset(null)}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>{previewAsset?.filename}</DialogTitle>
            <DialogDescription>
              {previewAsset?.original_width && previewAsset?.original_height
                ? `${previewAsset.original_width} x ${previewAsset.original_height}`
                : ""}{" "}
              {previewAsset ? `— ${formatFileSize(previewAsset.file_size_bytes)}` : ""}
            </DialogDescription>
          </DialogHeader>
          {(previewAsset?.full_url || previewAsset?.thumbnail_url) ? (
            <img
              src={previewAsset.full_url || previewAsset.thumbnail_url || ""}
              alt={previewAsset.alt_text || previewAsset.filename}
              className="w-full rounded-md object-contain max-h-[500px]"
            />
          ) : (
            <div className="flex items-center justify-center py-16 bg-muted rounded-md">
              <ImageIcon className="h-16 w-16 text-muted-foreground" />
            </div>
          )}
          {previewAsset && (previewAsset.categories.length > 0 || previewAsset.labels.length > 0) && (
            <div className="flex flex-wrap gap-1.5">
              {previewAsset.categories.map((c) => (
                <Badge key={c.id} variant="outline">{c.name}</Badge>
              ))}
              {previewAsset.labels.map((l) => (
                <Badge
                  key={l.id}
                  variant="secondary"
                  style={l.colour ? { backgroundColor: l.colour, color: "#fff" } : undefined}
                >
                  {l.name}
                </Badge>
              ))}
            </div>
          )}
          {previewAsset?.alt_text && (
            <p className="text-sm text-muted-foreground">{previewAsset.alt_text}</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
